import React from 'react';
import { Award, BookOpen } from 'lucide-react';
import { FormulaPathStep, Question } from '../../types';
import { VARIABLE_BY_ID } from '../../data/formulas';
import { Latex, InlineLatex } from './LatexRenderer';
import { FormulaQuestionCard } from '../FormulaQuestionCard';

interface FormulaStepCardProps {
  step: FormulaPathStep;
  stepIndex: number;
  totalSteps: number;
  questions: Question[];
}

export const FormulaStepCard: React.FC<FormulaStepCardProps> = ({
  step,
  stepIndex,
  totalSteps,
  questions,
}) => {
  return (
    <div className="rounded-xl border-2 border-emerald-500/70 bg-paper-50 p-4 shadow-sm dark:border-emerald-700/70 dark:bg-study-card space-y-3">
      {/* Step Index & Title */}
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-paper-200 pb-2.5 dark:border-study-line">
        <div>
          <div className="flex items-center gap-2">
            <span className="font-mono text-[11px] font-bold uppercase tracking-wider rounded bg-emerald-100 text-emerald-800 px-2 py-0.5 dark:bg-emerald-950 dark:text-emerald-300 border border-emerald-300 dark:border-emerald-800">
              Passo {stepIndex + 1} de {totalSteps}
            </span>
            <span className="text-xs font-mono font-bold text-zinc-500 dark:text-zinc-400">
              ID: {step.equation.id}
            </span>
          </div>
          <h3 className="font-serif text-sm font-bold text-zinc-900 dark:text-zinc-100 mt-1">
            {step.equation.name}
          </h3>
        </div>

        <div className="text-xs font-mono text-right">
          <span className="text-zinc-500 dark:text-zinc-400">Resolve para: </span>
          <span className="font-bold text-emerald-700 dark:text-emerald-300">
            {VARIABLE_BY_ID[step.calculatedVariable]?.name} (
            <InlineLatex>{VARIABLE_BY_ID[step.calculatedVariable]?.symbol ?? ''}</InlineLatex>)
          </span>
        </div>
      </div>

      {/* Center Rearranged Formula Card */}
      <div className="rounded-lg bg-paper-100 p-3 text-center dark:bg-study-slate border border-paper-200 dark:border-study-line">
        <span className="text-[10px] uppercase font-bold tracking-wider text-zinc-400 block mb-1">
          Equação Isolada para a Resolução:
        </span>
        <div className="font-mono text-lg text-indigo-700 dark:text-indigo-300 py-1">
          <Latex>{step.rearrangedLatex}</Latex>
        </div>
        <span className="text-[11px] font-mono text-zinc-500 dark:text-zinc-400 block mt-1">
          Forma canônica original: <InlineLatex>{step.equation.latex}</InlineLatex>
        </span>
      </div>

      {/* Plain-Portuguese Deduction Description */}
      <p className="text-xs leading-relaxed text-zinc-700 dark:text-zinc-300 font-sans">
        {step.equation.description}
      </p>

      {/* Grandezas Consumed */}
      <div className="flex flex-wrap items-center gap-1.5 pt-2 border-t border-paper-200 dark:border-study-line text-xs">
        <span className="text-[11px] font-bold text-zinc-400 uppercase tracking-wider">
          Consome dados:
        </span>
        {step.usingVariables.map((vid) => {
          const vr = VARIABLE_BY_ID[vid];
          if (!vr) return null;
          return (
            <span
              key={vid}
              className="rounded bg-paper-200 px-2 py-0.5 text-xs font-mono text-zinc-800 dark:bg-study-panel dark:text-zinc-200 border border-paper-300 dark:border-study-line"
            >
              <InlineLatex>{vr.symbol}</InlineLatex> {vr.name}
            </span>
          );
        })}
      </div>

      {/* Official Descritor Section */}
      <div className="rounded-lg border border-indigo-200 bg-indigo-50/50 p-3 dark:border-indigo-900/60 dark:bg-indigo-950/20 space-y-1">
        <div className="flex items-center gap-1.5 text-xs font-bold text-indigo-900 dark:text-indigo-300">
          <Award className="h-3.5 w-3.5 text-indigo-600 dark:text-indigo-400" />
          <span>Descritor Oficial do IFF (Anexo VI):</span>
          <span className="font-mono underline">{step.descriptorCode}</span>
        </div>
        <p className="text-[11px] font-semibold text-indigo-950 dark:text-indigo-200">
          {step.descriptorTheme}
        </p>
        <p className="text-[11px] text-zinc-600 dark:text-zinc-400 leading-relaxed">
          {step.descriptorDescription}
        </p>
        {step.alternateDescriptors && step.alternateDescriptors.length > 0 && (
          <div className="mt-1 pt-1 border-t border-indigo-200/60 dark:border-indigo-900/40 text-[10px] text-zinc-500">
            <span>
              Mapeamento alternativo:{' '}
              {step.alternateDescriptors.map((a) => a.code).join(', ')}
            </span>
          </div>
        )}
      </div>

      {/* Real Exam Questions Tagged with this Formula */}
      <div className="space-y-2 pt-2 border-t border-paper-200 dark:border-study-line">
        <div className="flex items-center justify-between">
          <span className="text-[11px] font-bold text-zinc-700 dark:text-zinc-300 uppercase tracking-wider flex items-center gap-1.5">
            <BookOpen className="h-3.5 w-3.5 text-indigo-600 dark:text-indigo-400" />
            Questões Reais do Vestibular com esta Fórmula ({questions.length})
          </span>
          <span className="text-[10px] text-zinc-400">
            Tag estrutural: {step.equation.id}
          </span>
        </div>

        {questions.length > 0 ? (
          <div className="space-y-2">
            {questions.map((q) => (
              <FormulaQuestionCard key={q.id} question={q} />
            ))}
          </div>
        ) : (
          <p className="rounded border border-paper-200 bg-paper-100 p-2.5 text-center text-xs text-zinc-500 dark:border-study-line dark:bg-study-panel dark:text-zinc-400">
            Nenhuma questão do banco cadastrada com a tag <code>{step.equation.id}</code> no momento.
          </p>
        )}
      </div>
    </div>
  );
};
